// Backward compatibility - Re-export from new modular Excel services
// This file maintains the original API while using the new organized structure

import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { formatCurrency } from '../component/withdrawal-check/utils';

export {
  exportMaturityScheduleToExcel,
  exportMultipleMaturitySchedulesToExcel,
  exportMultipleWithdrawalTransactionsToExcel,
  SingleMaturityExportService,
  MultiMaturityExportService,
  WithdrawalExportService,
  MultiWithdrawalExportService,
  BaseExcelService,
  ExcelColors,
} from './excel';

export type {
  ExcelCellStyle,
  MultiExportData,
  MultiWithdrawalExportData,
} from './excel';

export const exportMaturityPaymentsToExcel = async (
  records: any[],
  month: number,
  year: number
) => {
  const worksheet = XLSX.utils.json_to_sheet(
    records.map((record) => ({
      'Mã thanh toán': record.id,
      'Ngày thanh toán': dayjs(record.paymentDate).format('DD/MM/YYYY'),
      'Số tiền': record.amount,
      'Trạng thái':
        record.originalStatus === 'COMPLETED' ? 'Hoàn thành' : 'Chờ xử lý',
      'Nhân viên': record.employee?.name || 'N/A',
      'Khách hàng': record.customer?.fullName || 'N/A',
      'SĐT khách hàng': record.customer?.phoneNumber || 'N/A',
      'Mã kỳ đáo hạn': record.maturitySchedule?.id || 'N/A',
      'Ngày đáo hạn': record.maturitySchedule?.dueDate
        ? dayjs(record.maturitySchedule.dueDate).format('DD/MM/YYYY')
        : 'N/A',
      'Số thẻ': record.maturitySchedule?.creditCard?.cardNumber || 'N/A',
      'Chủ thẻ': record.maturitySchedule?.creditCard?.cardHolderName || 'N/A',
      'Thời gian tạo': dayjs(record.createdAt).format('DD/MM/YYYY HH:mm:ss'),
    }))
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Thanh toán đáo hạn');

  // Auto-fit columns
  const maxWidth = 20;
  const wscols = Object.keys(worksheet).map((key) => ({
    wch: Math.min(maxWidth, Math.max(10, key.length)),
  }));
  worksheet['!cols'] = wscols;

  // Save file
  XLSX.writeFile(workbook, `Bao_cao_thanh_toan_dao_han_${month}_${year}.xlsx`);
};

export const exportMaturityTransactionsToExcel = async (
  records: any[],
  month: number,
  year: number
) => {
  const worksheet = XLSX.utils.json_to_sheet(
    records.map((record) => ({
      'Mã giao dịch': record.id,
      'Ngày giao dịch': dayjs(record.transactionDate).format('DD/MM/YYYY'),
      'Số tiền': record.amount,
      'Phí KH': record.feeForCustomer
        ? record.customerType === 'vn'
          ? formatCurrency(record.feeForCustomer)
          : `${record.feeForCustomer}%`
        : 'N/A',
      'Thành tiền': record.amount * (record.feeForCustomer / 100),
      'Phí POS': record.feeApplied ? `${record.feeApplied}%` : 'N/A',
      'Phí CTV': record.commissionRateForPartner
        ? `${record.commissionRateForPartner}%`
        : 'N/A',
      'Thực nhận': record.balance,
      'Trạng thái': record.status === 'COMPLETED' ? 'Hoàn thành' : 'Đang chờ',
      'Số thẻ': record.creditCard?.cardNumber || 'N/A',
      'Chủ thẻ': record.creditCard?.cardHolderName || 'N/A',
      'Ngân hàng': record.creditCard?.bank?.name || 'N/A',
    }))
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Giao dịch đáo hạn');

  // Auto-fit columns
  const maxWidth = 20;
  const wscols = Object.keys(worksheet).map((key) => ({
    wch: Math.min(maxWidth, Math.max(10, key.length)),
  }));
  worksheet['!cols'] = wscols;

  // Save file
  XLSX.writeFile(workbook, `Bao_cao_giao_dich_dao_han_${month}_${year}.xlsx`);
};

export const exportFinancialRecordsToExcel = async (
  records: any[],
  month: number,
  year: number
) => {
  const worksheet = XLSX.utils.json_to_sheet(
    records.map((record) => ({
      'Mã bản ghi': record.id,
      Ngày: dayjs(record.date).format('DD/MM/YYYY'),
      Loại: record.type === 'INCOME' ? 'Thu' : 'Chi',
      'Số tiền': record.amount,
      'Mô tả': record.description || 'N/A',
      'Người tạo': record.createdBy || 'N/A',
      'Thời gian tạo': dayjs(record.createdAt).format('DD/MM/YYYY HH:mm:ss'),
    }))
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Báo cáo thu chi');

  // Auto-fit columns
  const maxWidth = 20;
  const wscols = Object.keys(worksheet).map((key) => ({
    wch: Math.min(maxWidth, Math.max(10, key.length)),
  }));
  worksheet['!cols'] = wscols;

  // Save file
  XLSX.writeFile(workbook, `Bao_cao_thu_chi_${month}_${year}.xlsx`);
};

// Interface cho combined record
interface CombinedRecord {
  id: number;
  date: string;
  type: 'transaction' | 'financial';
  subType: string;
  amount: number;
  description: string;
  employee?: { name: string };
  status?: string;
  creditCard?: {
    cardNumber: string;
    cardHolderName?: string;
    bank?: { name: string };
  };
  feeApplied?: number;
  posDevice?: { deviceCode: string };
  recordType?: string;
  category?: string;
  createdBy?: string;
}

export const exportCombinedDataToExcel = async (
  records: CombinedRecord[],
  day: number,
  month: number,
  year: number
) => {
  const transactionRecords = records.filter((r) => r.type === 'transaction');
  const financialRecords = records.filter((r) => r.type === 'financial');

  // Tạo workbook với nhiều sheet
  const workbook = XLSX.utils.book_new();

  // Sheet 1: Tổng hợp
  const summaryData = records.map((record) => ({
    'Thời gian': dayjs(record.date).format('DD/MM/YYYY HH:mm'),
    Loại:
      record.type === 'transaction'
        ? record.subType === 'withdrawal'
          ? 'Rút tiền'
          : 'Nạp tiền'
        : record.subType === 'INCOME'
          ? 'Thu'
          : 'Chi',
    'Số tiền': record.amount,
    'Mô tả': record.description,
    'Người thực hiện':
      record.type === 'transaction'
        ? record.employee?.name || 'N/A'
        : record.createdBy || 'N/A',
    'Trạng thái/Thông tin':
      record.type === 'transaction'
        ? record.status === 'COMPLETED'
          ? 'Hoàn thành'
          : 'Đang chờ'
        : record.category || 'N/A',
  }));

  const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Tổng hợp');

  // Sheet 2: Giao dịch (nếu có)
  if (transactionRecords.length > 0) {
    const transactionData = transactionRecords.map((record) => ({
      'Mã giao dịch': record.id,
      'Ngày giao dịch': dayjs(record.date).format('DD/MM/YYYY'),
      Loại: record.subType === 'withdrawal' ? 'Rút tiền' : 'Nạp tiền',
      'Số tiền': record.amount,
      'Phí POS': record.feeApplied ? `${record.feeApplied}%` : 'N/A',
      POS: record.posDevice?.deviceCode || 'N/A',
      'Trạng thái': record.status === 'COMPLETED' ? 'Hoàn thành' : 'Đang chờ',
      'Số thẻ': record.creditCard?.cardNumber || 'N/A',
      'Chủ thẻ': record.creditCard?.cardHolderName || 'N/A',
      'Ngân hàng': record.creditCard?.bank?.name || 'N/A',
      'Nhân viên': record.employee?.name || 'N/A',
    }));

    const transactionWorksheet = XLSX.utils.json_to_sheet(transactionData);
    XLSX.utils.book_append_sheet(workbook, transactionWorksheet, 'Giao dịch');
  }

  // Sheet 3: Thu chi (nếu có)
  if (financialRecords.length > 0) {
    const financialData = financialRecords.map((record) => ({
      'Mã bản ghi': record.id,
      Ngày: dayjs(record.date).format('DD/MM/YYYY'),
      Loại: record.subType === 'INCOME' ? 'Thu' : 'Chi',
      'Số tiền': record.amount,
      'Mô tả': record.description,
      'Danh mục': record.category || 'N/A',
      'Người tạo': record.createdBy || 'N/A',
    }));

    const financialWorksheet = XLSX.utils.json_to_sheet(financialData);
    XLSX.utils.book_append_sheet(workbook, financialWorksheet, 'Thu chi');
  }

  // Auto-fit columns cho tất cả sheets
  const allWorksheets = [summaryWorksheet];
  if (transactionRecords.length > 0) {
    allWorksheets.push(workbook.Sheets['Giao dịch']);
  }
  if (financialRecords.length > 0) {
    allWorksheets.push(workbook.Sheets['Thu chi']);
  }

  allWorksheets.forEach((worksheet) => {
    if (worksheet) {
      const maxWidth = 20;
      const wscols = Object.keys(worksheet).map((key) => ({
        wch: Math.min(maxWidth, Math.max(10, key.length)),
      }));
      worksheet['!cols'] = wscols;
    }
  });

  // Save file
  const fileName = `Bao_cao_tong_hop_${day}_${month}_${year}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};
